/**
 * 工具列的「檔案」下拉選單。
 *
 * 交換格式的匯入匯出原本只掛在 macOS 原生選單上，瀏覽器宿主完全沒有入口。
 * 這裡改成讀 `AppPlatform` 的選用方法：宿主有實作才顯示，desktop 與 web
 * 因此可以共用同一份工具列而不必各自維護一份項目清單。
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';

import type { AppPlatform } from '../../platform/ports';

/** 工具列只需要 platform 的交換格式方法，其餘（開檔、存檔）由既有按鈕負責。 */
export type FileExchangePlatform = Pick<
  AppPlatform,
  'importMspdi' | 'exportMspdi' | 'importXlsx' | 'exportXlsx' | 'exportWbsXlsx' | 'exportCsv'
>;

interface FileMenuEntry {
  readonly key: keyof FileExchangePlatform;
  readonly label: string;
  readonly testId: string;
  /** 匯入會取代專案內容，唯讀時必須停用；匯出不改動專案，永遠可用。 */
  readonly isImport: boolean;
}

/** 文案與順序對齊原生選單（`store/menu.ts` 的「檔案」子選單）。 */
const ENTRIES: readonly FileMenuEntry[] = [
  { key: 'importMspdi', label: '匯入 MSPDI…', testId: 'file-menu-import-mspdi', isImport: true },
  { key: 'exportMspdi', label: '匯出 MSPDI…', testId: 'file-menu-export-mspdi', isImport: false },
  { key: 'importXlsx', label: '匯入 XLSX…', testId: 'file-menu-import-xlsx', isImport: true },
  { key: 'exportXlsx', label: '匯出 XLSX…', testId: 'file-menu-export-xlsx', isImport: false },
  { key: 'exportWbsXlsx', label: '匯出 WBS 清單（Excel）…', testId: 'file-menu-export-wbs-xlsx', isImport: false },
  { key: 'exportCsv', label: '匯出 CSV…', testId: 'file-menu-export-csv', isImport: false },
];

export interface FileMenuProps {
  readonly platform: FileExchangePlatform;
  readonly readOnly: boolean;
}

export function FileMenu({ platform, readOnly }: FileMenuProps): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent): void => {
      if (containerRef.current?.contains(event.target as Node) !== true) setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    containerRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')?.focus();
  }, [isOpen]);

  const entries = ENTRIES.filter((entry) => platform[entry.key] !== undefined);
  if (entries.length === 0) return null;

  const close = (): void => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [...(containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [])];
    if (items.length === 0) return;
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(current + offset + items.length) % items.length]?.focus();
  };

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        data-testid="toolbar-file-menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
      >
        檔案 ▾
      </button>
      {isOpen ? (
        <div
          role="menu"
          aria-label="檔案"
          data-testid="file-menu"
          className="absolute right-0 z-20 mt-1 min-w-[13rem] rounded border border-slate-300 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800"
        >
          {entries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="menuitem"
              data-testid={entry.testId}
              disabled={readOnly && entry.isImport}
              onClick={() => {
                setIsOpen(false);
                void platform[entry.key]?.();
              }}
              className="block w-full px-3 py-1 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-700"
            >
              {entry.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
